import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useCallback } from "react";
import { PrayerTime } from "@/types/page";

interface UseDatabaseOperationsProps {
  currentUser: any;
}

export function useDatabaseOperations({ currentUser }: UseDatabaseOperationsProps) {
  const { toast } = useToast();

  const savePrayerTimesToDatabase = useCallback(async (
    blockTitle: string,
    animationSpeed: number,
    prayerTimes: PrayerTime[]
  ) => {
    if (!currentUser?.id) {
      console.log("No user logged in, skipping database save");
      return;
    }

    try {
      console.log("Saving prayer times data directly to database for user:", currentUser.id);
      
      // Clean prayer times array by ensuring string time values
      const cleanPrayerTimes = prayerTimes.map(pt => {
        // Ensure all time values are strings and convert if needed
        const time = typeof pt.time === 'string' ? pt.time : String(pt.time);
        
        // Omit the legacyFieldName property for clean data
        const { legacyFieldName, ...rest } = pt as any;
        return {
          ...rest,
          time: time,
          // Ensure ID is always a string
          id: pt.id.toString()
        };
      });

      const data = {
        blockTitle,
        animationSpeed,
        prayerTimes: cleanPrayerTimes
      };
      
      console.log("Prepared data for database:", data);
      
      // Save directly to the user_settings table
      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: currentUser.id,
          settings_key: 'prayer_times_data',
          settings_value: data
        }, {
          onConflict: 'user_id, settings_key'
        });
        
      if (error) {
        console.error('Error saving prayer times to database:', error);
        throw error;
      } else {
        console.log('Prayer times saved to database successfully');
      }
      
      // Update localStorage timestamp to trigger refresh on other components
      localStorage.setItem('prayer-times-last-save', new Date().toISOString());
      console.log('Updated local storage timestamp');

      return true;
    } catch (err) {
      console.error('Error saving prayer times to database:', err);
      toast({
        title: "שגיאה",
        description: "אירעה שגיאה בעת שמירת הנתונים למסד הנתונים",
      });
      return false;
    }
  }, [currentUser, toast]);

  return {
    savePrayerTimesToDatabase
  };
}
